import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { MapPin, Bell, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WaitlistForm = ({ zipCode, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    zip_code: zipCode || '',
    city: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.zip_code) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/waitlist`, formData);
      setSubmitted(true);
      toast.success("You're on the waitlist!");
      if (onSuccess) onSuccess();
    } catch (err) {
      if (err.response?.status === 200 || err.response?.data?.id) {
        // Already on waitlist
        setSubmitted(true);
        toast.info("You're already on our waitlist!");
      } else {
        toast.error('Failed to join waitlist. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center"
        data-testid="waitlist-success"
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-green-800 mb-2">You're on the List!</h3>
        <p className="text-green-700">
          We'll notify you at <strong>{formData.email}</strong> as soon as we expand to ZIP code <strong>{formData.zip_code}</strong>.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-8"
      data-testid="waitlist-form"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
          <Bell className="h-6 w-6 text-[#F56F05]" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-zinc-900">Join Our Expansion Waitlist</h3>
          <p className="text-sm text-zinc-600">We're not in your area yet, but you can be the first to know when we arrive!</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="waitlist-name">Full Name *</Label>
            <Input
              id="waitlist-name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Jane Smith"
              required
              data-testid="waitlist-name"
            />
          </div>
          <div>
            <Label htmlFor="waitlist-email">Email *</Label>
            <Input
              id="waitlist-email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="jane@example.com"
              required
              data-testid="waitlist-email"
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="waitlist-phone">Phone (Optional)</Label>
            <Input
              id="waitlist-phone"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="(555) 123-4567"
              data-testid="waitlist-phone"
            />
          </div>
          <div>
            <Label htmlFor="waitlist-zip">ZIP Code *</Label>
            <Input
              id="waitlist-zip"
              value={formData.zip_code}
              onChange={(e) => setFormData(prev => ({ ...prev, zip_code: e.target.value }))}
              placeholder="90210"
              required
              data-testid="waitlist-zip"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="waitlist-city">City (Optional)</Label>
          <Input
            id="waitlist-city"
            value={formData.city}
            onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
            placeholder="Beverly Hills"
            data-testid="waitlist-city"
          />
        </div>

        <div className="flex gap-3 pt-4">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="flex-1"
              data-testid="waitlist-cancel"
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            disabled={submitting}
            className="flex-1 bg-[#F56F05] hover:bg-[#C2410C]"
            data-testid="waitlist-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Joining...
              </>
            ) : (
              <>
                <Bell className="h-4 w-4 mr-2" />
                Join Waitlist
              </>
            )}
          </Button>
        </div>
      </form>

      <p className="text-xs text-zinc-500 mt-4 text-center">
        We'll only contact you about expansion updates. No spam, ever.
      </p>
    </motion.div>
  );
};

export default WaitlistForm;
